import { ECharts, init, graphic } from 'echarts';
import React, { useEffect, useRef } from 'react';

interface ScoreColor {
  [key: string]: { text: string; color: string };
}

export interface CreditScoreMeterProps {
  score?: number;
}

const MAX_SCORE = 850;
const MIN_SCORE = 300;
const CANVAS_WIDTH = 288;
const CANVAS_HEIGHT = 270;
const START_ANGLE = 220;
const END_ANGLE = 320;

export const CreditScoreMeter: React.FC<CreditScoreMeterProps> = ({
  score,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<ECharts>();

  useEffect(() => {
    if (chartRef.current) {
      chartInstance.current = init(chartRef.current);

      const scoreText: ScoreColor = {
        '- - - - -': { text: '- - - - -', color: '#000000' },
        'Very Poor': { text: 'Very Poor', color: '#dc0015' },
        Poor: { text: 'Poor', color: '#f57c00' },
        Fair: { text: 'Fair', color: '#000000' },
        Good: { text: 'Good', color: '#81b44c' },
        Excellent: { text: 'Excellent', color: '#638a3a' },
      };

      const getScoreText = (score: number | undefined) => {
        if (!score) return '- - - - -';
        if (score >= 300 && score <= 600) return 'Very Poor';
        if (score >= 601 && score <= 657) return 'Poor';
        if (score >= 658 && score <= 719) return 'Fair';
        if (score >= 720 && score <= 780) return 'Good';
        if (score >= 781) return 'Excellent';
        return '- - - - -';
      };

      const currentScoreText = getScoreText(score);
      const currentScore = scoreText[currentScoreText];

      const option = {
        series: [
          {
            type: 'gauge',
            center: ['50%', '50%'],
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
            min: MIN_SCORE,
            max: MAX_SCORE,
            radius: '90%',
            backgroundStyle: {
              color: '#E2E2E2',
            },
            itemStyle: {
              color: new graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: '#BC0000' },
                { offset: 0.16, color: 'rgba(188, 0, 0, 0.7)' },
                { offset: 0.33, color: '#E26F33' },
                { offset: 0.5, color: 'rgba(226, 111, 51, 0.7)' },
                { offset: 0.66, color: '#D9C536' },
                { offset: 0.83, color: 'rgba(217, 197, 54, 0.7)' },
                { offset: 1, color: '#81B44C' },
              ]),
              emphasis: {
                color: new graphic.LinearGradient(0, 0, 1, 0, [
                  { offset: 0, color: '#BC0000' },
                  { offset: 0.16, color: 'rgba(188, 0, 0, 0.7)' },
                  { offset: 0.33, color: '#E26F33' },
                  { offset: 0.5, color: 'rgba(226, 111, 51, 0.7)' },
                  { offset: 0.66, color: '#D9C536' },
                  { offset: 0.83, color: 'rgba(217, 197, 54, 0.7)' },
                  { offset: 1, color: '#81B44C' },
                ]),
              },
            },
            progress: {
              show: true,
              width: 15,
              silent: true,
            },
            pointer: {
              show: false,
            },
            axisLine: {
              lineStyle: {
                width: 15,
              },
            },
            axisTick: {
              distance: -27,
              splitNumber: 10,
              lineStyle: {
                width: 2,
                color: '#E2E2E2',
              },
            },
            splitLine: {
              show: false,
            },
            axisLabel: {
              show: false,
            },
            anchor: {
              show: false,
            },
            title: {
              show: false,
            },
            detail: {
              show: false,
            },
            data: [
              {
                value: score,
              },
            ],
          },
        ],
        graphic: [
          {
            type: 'text',
            left: 'center',
            top: '30%',
            z: 2,
            style: {
              text: score,
              fontSize: 64,
              fontWeight: 'bold',
              fill: '#333',
            },
          },
          {
            type: 'text',
            left: 'center',
            top: '56.5%',
            z: 2,
            style: {
              text: currentScoreText,
              fontSize: 24,
              fontWeight: 'bold',
              fill: currentScore.color,
              padding: [5, 10],
              borderRadius: 5,
            },
          },
        ],
      };

      chartInstance.current.resize({
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
      });

      chartInstance.current.setOption(option);

      const resizeChart = () => {
        if (chartInstance.current) chartInstance.current.resize();
      };

      window.addEventListener('resize', resizeChart);

      return () => {
        window.removeEventListener('resize', resizeChart);
        if (chartInstance.current) chartInstance.current.dispose();
      };
    }
  }, [score]);

  return (
    <div
      className="temperature-gauge"
      ref={chartRef}
      style={{ width: '300px', height: '250px', margin: 'auto' }}
    />
  );
};
